// contexts/SignUpContext.tsx
import { AboutYou } from "@/components/Auth/Onboarding/AboutYou";
import { ChooseYourPlan } from "@/components/Auth/Onboarding/ChooseYourPlan";
import { WhereDidYouHearAboutUs } from "@/components/Auth/Onboarding/WhereDidYouHearAboutUs";
import SignUpForm from "@/components/Auth/SignUpForm";
import { useStores } from "@/hooks/useStores";
import { observer } from "mobx-react";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

export type SignUpStep = {
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
  isSkippable?: boolean;
  hidden?: boolean;
  completed: boolean;
  skip?: boolean;
};

interface SignUpContextType {
  currentStepIndex: number;
  setCurrentStepIndex: (index: number) => void;
  steps: SignUpStep[];
  otherText: string;
  setOtherText: (text: string) => void;
  stepCompletion: Record<string, boolean>;
  setStepCompleted: (stepKey: string, completed: boolean) => void;
}

const SignUpContext = createContext<SignUpContextType | undefined>(undefined);

export const SignUpProvider = observer(
  ({ children }: { children: ReactNode }) => {
    const { authStore } = useStores();

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [otherText, setOtherText] = useState("");
    const [hasCouponCode, setHasCouponCode] = useState<boolean>(false);
    const currentUser = authStore.currentUser;

    const [stepCompletion, setStepCompletion] = useState<
      Record<string, boolean>
    >({});

    useEffect(() => {
      setHasCouponCode(!!sessionStorage.getItem("couponCode"));
    }, []);

    const hasCompletedOnBoarding =
      authStore.currentUser?.hasCompletedOnboarding;

    const setStepCompleted = (stepKey: string, completed: boolean) => {
      setStepCompletion((prev) => ({
        ...prev,
        [stepKey]: completed,
      }));
    };

    const steps: SignUpStep[] = [
      {
        key: "signUpForm",
        component: SignUpForm,
        completed: false,
        skip: currentUser || hasCompletedOnBoarding,
        hidden: currentUser,
      },
      {
        key: "onboardingAboutYou",
        component: AboutYou,
        completed: false,
        skip: hasCompletedOnBoarding,
      },
      {
        key: "onboardingSource",
        component: WhereDidYouHearAboutUs,
        completed: false,
        skip: hasCompletedOnBoarding,
      },
      {
        key: "onboardingChooseYourPlan",
        component: ChooseYourPlan,
        completed: false,
        skip: currentUser?.plan === 2,
      },
    ];

    return (
      <SignUpContext.Provider
        value={{
          currentStepIndex,
          setCurrentStepIndex,
          steps,
          otherText,
          setOtherText,
          stepCompletion,
          setStepCompleted,
        }}
      >
        {children}
      </SignUpContext.Provider>
    );
  }
);

export const useSignUpContext = () => {
  const context = useContext(SignUpContext);
  if (!context) {
    throw new Error("useSignUpContext must be used within a SignUpProvider");
  }
  return context;
};
